import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Router, 
  ArrowRight,
  HardDrive,
  BarChart3
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

const StatCard = ({ title, value, icon: Icon, color, loading }) => {
  const colorClasses = {
    info: 'text-cyan-600',
    success: 'text-green-600',
    primary: 'text-blue-600'
  };
  
  return (
    <Card className="h-full border border-gray-200 rounded-lg">
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-4">
          <div className="text-sm text-gray-600">
            {loading ? <Skeleton className="w-20 h-4" /> : title}
          </div>
          {loading ? (
            <Skeleton className="w-6 h-6 rounded-full" />
          ) : (
            <Icon className={`w-5 h-5 ${colorClasses[color]}`} />
          )}
        </div>
        {loading ? (
          <Skeleton className="w-16 h-8" />
        ) : (
          <div className="text-2xl font-medium text-gray-900">
            {value}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function IoTGatewaysWidget({ loading = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Mock data - in a real app, this would come from an API
  const gatewayData = {
    total: 16,
    active: 12,
    dataProcessed: "1.8 TB"
  };
  
  const handleNavigateToIoTGateways = () => {
    navigate('/dashboard/iot-gateways');
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.5 }}
      className="h-full"
    >
      <Card className="h-full rounded-xl border border-gray-200 overflow-visible relative">
        <CardContent className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {loading ? <Skeleton className="w-32 h-6" /> : t('dashboard.iotGateways.title')}
            </h3>
            <Button 
              variant="ghost"
              size="sm"
              onClick={handleNavigateToIoTGateways}
              disabled={loading}
              className="text-blue-600 hover:text-blue-700"
            >
              {loading ? <Skeleton className="w-20 h-4" /> : (
                <>
                  {t('dashboard.general.viewAll', 'View All')}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <StatCard 
              title={t('dashboard.iotGateways.total')} 
              value={gatewayData.total} 
              icon={Router}
              color="info"
              loading={loading}
            />
            <StatCard 
              title={t('dashboard.iotGateways.active')} 
              value={gatewayData.active} 
              icon={HardDrive}
              color="success"
              loading={loading}
            />
            <StatCard 
              title={t('dashboard.iotGateways.dataProcessed')} 
              value={gatewayData.dataProcessed} 
              icon={BarChart3}
              color="primary"
              loading={loading}
            />
          </div>
          
          {!loading && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <div className="text-sm font-medium text-gray-900 mb-3">
                Gateway Distribution
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2 text-center bg-cyan-100 text-cyan-800 rounded">
                  <div className="text-xs">Region 1</div>
                  <div className="text-sm font-bold">6</div>
                </div>
                <div className="p-2 text-center bg-green-100 text-green-800 rounded">
                  <div className="text-xs">Region 2</div>
                  <div className="text-sm font-bold">7</div>
                </div>
                <div className="p-2 text-center bg-yellow-100 text-yellow-800 rounded">
                  <div className="text-xs">Region 3</div>
                  <div className="text-sm font-bold">3</div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
} 